/**
 * Vetvy — splittery zoskupené podľa nadradeného.
 *
 * V kazete sú dva druhy splitterov: nadradený (rozvodný, `is_customer_splitter`
 * nie je true) rozdelí vlákno na N vetiev, a zákaznícky, na ktorom už visia
 * ľudia. Zoznam stĺpikov ich ukazuje po jednom a nie je z neho vidieť, čo
 * z čoho visí.
 *
 * Ako sa väzba zisťuje: nadradený splitter zapisuje do svojich výstupov dvojicu
 * (cable_id, fiber) a zákaznícky má input_cable_id + input_fiber. Dvojica je
 * teda spojka. Priamym porovnaním sa v produkcii trafí asi dve tretiny —
 * zvyšku vlákno cestou prechádza cez priechodné kazety, takže sa dopátra cez
 * traceOpticalPath(), ktorý celú optickú cestu vie prejsť.
 */
import React, { useMemo, useState } from 'react';
import { Pole, Cable } from '../../types';
import { traceOpticalPath } from '../../utils/opticalPathTracer';
import { farbaVlakna } from '../../utils/customerSearch';
import { poleTypeIcon } from '../../utils/poleLabels';
import './SplitterBranches.css';

interface Props {
  poles: Pole[];
  cables: Cable[];
  onOpenPole?: (pole: Pole) => void;
}

/** Zákaznícky splitter aj s tým, odkiaľ je kŕmený. */
interface Dieta {
  pole: Pole;
  /** skutočný stĺpik — pri virtuálnej zákazníckej kazete jej rodič */
  stlpik: Pole;
  typ: string | null;
  vlakno: number | null;
  zakaznikov: number;
  vystupov: number;
  /** true = nadradený sa našiel až dotrasovaním, nie priamo */
  dotrasovane: boolean;
}

interface Vetva {
  klucStlp: number;
  stlp: Pole;
  splitterIndex: number;
  typ: string | null;
  farba: string | null;
  vystupovSpolu: number;
  vystupovObsadenych: number;
  deti: Dieta[];
}

const jeZakaznicky = (sp: any) => sp?.is_customer_splitter === true;

export const SplitterBranches: React.FC<Props> = ({ poles, cables, onOpenPole }) => {
  const [rozbalene, setRozbalene] = useState<Set<string>>(new Set());
  const [hladane, setHladane] = useState('');

  const { vetvy, bezNadradeneho, dotrasovanych } = useMemo(() => {
    const zivé = poles.filter((p) => !(p as any).deleted_at);

    // 1) mapa výstupov nadradených splitterov: "kábel:vlákno" -> vetva
    const vetvyList: Vetva[] = [];
    const podlaKabla = new Map<string, Vetva>();

    zivé.forEach((p) => {
      ((p.cassette?.splitters || []) as any[]).forEach((sp: any, idx: number) => {
        if (jeZakaznicky(sp)) return;
        const vystupy = (sp.outputs || []) as any[];
        const vetva: Vetva = {
          klucStlp: p.id,
          stlp: p,
          splitterIndex: idx,
          typ: sp.type || null,
          farba: sp.color || null,
          vystupovSpolu: vystupy.length,
          vystupovObsadenych: vystupy.filter((o) => o?.cable_id || o?.customer_name).length,
          deti: [],
        };
        vetvyList.push(vetva);
        vystupy.forEach((o: any) => {
          if (o?.cable_id && o?.fiber) {
            podlaKabla.set(`${o.cable_id}:${o.fiber}`, vetva);
          }
        });
      });
    });

    // 2) zákaznícke splittery priradiť k vetve
    const siroty: Dieta[] = [];
    let pocetDotrasovanych = 0;

    zivé.forEach((p) => {
      const stlpik = p.is_virtual && p.parent_pole_id
        ? zivé.find((x) => x.id === p.parent_pole_id) || p
        : p;

      ((p.cassette?.splitters || []) as any[]).forEach((sp: any) => {
        if (!jeZakaznicky(sp)) return;
        const vystupy = (sp.outputs || []) as any[];
        const dieta: Dieta = {
          pole: p,
          stlpik,
          typ: sp.type || null,
          vlakno: sp.input_fiber ?? null,
          zakaznikov: vystupy.filter((o: any) => o?.customer_name).length,
          vystupov: vystupy.length,
          dotrasovane: false,
        };

        const kabel = sp.input_cable_id;
        const vlakno = sp.input_fiber;
        if (!kabel || !vlakno) {
          siroty.push(dieta);
          return;
        }

        let vetva = podlaKabla.get(`${kabel}:${vlakno}`);

        // Priame porovnanie nestačí, keď vlákno cestou prechádza kazetami —
        // vtedy sa ide po optickej ceste a hľadá sa posledný splitter pred cieľom.
        if (!vetva) {
          try {
            const cesta = traceOpticalPath(kabel, vlakno, poles, cables);
            if (cesta.found) {
              for (let i = cesta.segments.length - 1; i >= 0; i--) {
                const seg = cesta.segments[i];
                if (seg.type === 'splitter' && seg.poleId && seg.poleId !== p.id) {
                  vetva = vetvyList.find((v) => v.klucStlp === seg.poleId);
                  if (vetva) {
                    dieta.dotrasovane = true;
                    pocetDotrasovanych++;
                  }
                  break;
                }
              }
            }
          } catch {
            /* keď trasovanie spadne, splitter jednoducho zostane nezaradený */
          }
        }

        if (vetva) vetva.deti.push(dieta);
        else siroty.push(dieta);
      });
    });

    vetvyList.forEach((v) =>
      v.deti.sort((a, b) => (a.vlakno ?? 99) - (b.vlakno ?? 99)));
    vetvyList.sort((a, b) => a.stlp.name.localeCompare(b.stlp.name, 'sk', { numeric: true }));
    siroty.sort((a, b) => a.stlpik.name.localeCompare(b.stlpik.name, 'sk', { numeric: true }));

    return { vetvy: vetvyList, bezNadradeneho: siroty, dotrasovanych: pocetDotrasovanych };
  }, [poles, cables]);

  const q = hladane.trim().toLowerCase();
  const sediNaVetvu = (v: Vetva) =>
    !q || v.stlp.name.toLowerCase().includes(q)
      || v.deti.some((d) => d.stlpik.name.toLowerCase().includes(q));
  const zobrazene = vetvy.filter(sediNaVetvu);

  const prepni = (kluc: string) => {
    const n = new Set(rozbalene);
    if (n.has(kluc)) n.delete(kluc); else n.add(kluc);
    setRozbalene(n);
  };

  const zakaznikovVoVetve = (v: Vetva) => v.deti.reduce((s, d) => s + d.zakaznikov, 0);
  const vystupovVoVetve = (v: Vetva) => v.deti.reduce((s, d) => s + d.vystupov, 0);

  if (vetvy.length === 0 && bezNadradeneho.length === 0) {
    return (
      <div className="vetvy-prazdne">
        V tomto projekte nie sú žiadne splittery.
      </div>
    );
  }

  return (
    <div className="vetvy">
      <div className="vetvy-hlavicka">
        <input
          type="search"
          className="vetvy-hladanie"
          value={hladane}
          onChange={(e) => setHladane(e.target.value)}
          placeholder="Hľadať stĺpik vo vetvách…"
        />
        <div className="vetvy-suhrn">
          {vetvy.length} {vetvy.length === 1 ? 'vetva' : vetvy.length < 5 ? 'vetvy' : 'vetiev'}
          {dotrasovanych > 0 && <> · {dotrasovanych} dopátraných cez optickú cestu</>}
          {bezNadradeneho.length > 0 && <> · {bezNadradeneho.length} bez nadradeného</>}
        </div>
      </div>

      {zobrazene.map((v) => {
        const kluc = `${v.klucStlp}-${v.splitterIndex}`;
        const otvorene = rozbalene.has(kluc) || !!q;
        const volnych = v.vystupovSpolu - v.vystupovObsadenych;
        return (
          <div className="vetva" key={kluc} style={v.farba ? { borderLeftColor: v.farba } : undefined}>
            <button className="vetva-hlava" onClick={() => prepni(kluc)} aria-expanded={otvorene}>
              <span className="vetva-sipka">{otvorene ? '▾' : '▸'}</span>
              <span className="vetva-ikona">📦</span>
              <span className="vetva-nazov">{v.stlp.name}</span>
              {v.typ && (
                <span className="vetva-typ" style={v.farba ? { background: v.farba } : undefined}>
                  {v.typ}
                </span>
              )}
              <span className="vetva-cisla">
                {v.vystupovObsadenych}/{v.vystupovSpolu}
                <span className="vetva-zakaznici">
                  👤 {zakaznikovVoVetve(v)}/{vystupovVoVetve(v)}
                </span>
              </span>
            </button>

            {otvorene && (
              <div className="vetva-deti">
                {v.deti.length === 0 && (
                  <div className="vetva-dieta prazdne">Zatiaľ z nej nič nevisí.</div>
                )}
                {v.deti.map((d, i) => {
                  const f = farbaVlakna(d.vlakno);
                  return (
                    <button
                      className="vetva-dieta"
                      key={`${d.pole.id}-${i}`}
                      onClick={() => onOpenPole?.(d.stlpik)}
                    >
                      <span className="dieta-nazov">
                        {poleTypeIcon(d.stlpik.pole_type)} {d.stlpik.name}
                        {d.typ && <span className="dieta-typ">{d.typ}</span>}
                        {d.dotrasovane && (
                          <span className="dieta-dotrasovane" title="Nadradený sa našiel dopátraním optickej cesty">
                            trasa
                          </span>
                        )}
                      </span>
                      {f && (
                        <span className="dieta-vlakno" style={{ background: f.hex, color: f.text }}>
                          #{d.vlakno} {f.nazov}
                        </span>
                      )}
                      <span className={`dieta-obsadenost ${d.zakaznikov === 0 ? 'nula' : ''}`}>
                        {d.zakaznikov}/{d.vystupov}
                      </span>
                    </button>
                  );
                })}
                {volnych > 0 && (
                  <div className="vetva-volne">
                    {volnych} {volnych === 1 ? 'výstup voľný' : volnych < 5 ? 'výstupy voľné' : 'výstupov voľných'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {bezNadradeneho.length > 0 && !q && (
        <div className="vetva vetva-siroty">
          <button className="vetva-hlava" onClick={() => prepni('__siroty')}
                  aria-expanded={rozbalene.has('__siroty')}>
            <span className="vetva-sipka">{rozbalene.has('__siroty') ? '▾' : '▸'}</span>
            <span className="vetva-ikona">❓</span>
            <span className="vetva-nazov">Bez nadradeného</span>
            <span className="vetva-cisla">{bezNadradeneho.length}</span>
          </button>
          {rozbalene.has('__siroty') && (
            <div className="vetva-deti">
              <div className="vetva-dieta prazdne">
                Týmto splitterom sa nepodarilo dohľadať nadradený ani po optickej ceste —
                býva to nedokončené zapojenie vstupu.
              </div>
              {bezNadradeneho.map((d, i) => (
                <button className="vetva-dieta" key={`s-${d.pole.id}-${i}`}
                        onClick={() => onOpenPole?.(d.stlpik)}>
                  <span className="dieta-nazov">
                    {poleTypeIcon(d.stlpik.pole_type)} {d.stlpik.name}
                    {d.typ && <span className="dieta-typ">{d.typ}</span>}
                  </span>
                  <span className={`dieta-obsadenost ${d.zakaznikov === 0 ? 'nula' : ''}`}>
                    {d.zakaznikov}/{d.vystupov}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SplitterBranches;
